import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, addDoc, doc, updateDoc, deleteDoc, serverTimestamp, getDocs, query, orderBy } from 'firebase/firestore';
import { auth, db } from '../firebase';
import './Blogpost.css';

const Blogpost = () => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    tags: '',
    excerpt: '',
    seoKeywords: '',
    metaDescription: '',
    slug: '',
    mainImage: null,
    mainImageAlt: '',
    bannerImage: null,
    bannerImageAlt: '',
    contentImages: [],
    contentImagesAlt: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewImages, setPreviewImages] = useState({
    mainImage: null,
    bannerImage: null,
    contentImages: []
  });
  const [editMode, setEditMode] = useState(false);
  const [blogPostId, setBlogPostId] = useState(null);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [metaDescriptionManuallyEdited, setMetaDescriptionManuallyEdited] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [categoryLoading, setCategoryLoading] = useState(false);
  const editorRef = useRef(null);
  const toolbarRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Default fitness categories (will be replaced by Firestore data)
  const defaultFitnessCategories = [
    'Workout Routines',
    'Nutrition',
    'Weight Training',
    'Cardio',
    'Yoga & Flexibility',
    'Strength Training',
    'Weight Loss',
    'Muscle Building',
    'Fitness Tips',
    'Exercise Guides',
    'Health & Wellness',
    'Sports Performance',
    'Recovery & Rest',
    'Fitness Equipment',
    'Motivation & Mindset'
  ];

  // Load categories from Firestore
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setCategoryLoading(true);
      const categoriesRef = collection(db, 'categories');
      const q = query(categoriesRef, orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        // If no categories exist, initialize with default fitness categories
        const defaultCats = defaultFitnessCategories.map(name => ({ name }));
        const promises = defaultCats.map(cat => addDoc(collection(db, 'categories'), cat));
        await Promise.all(promises);
        setCategories(defaultFitnessCategories);
      } else {
        const cats = [];
        querySnapshot.forEach((doc) => {
          cats.push(doc.data().name);
        });
        setCategories(cats);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback to default categories if Firestore fails
      setCategories(defaultFitnessCategories);
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      setError('Category name cannot be empty');
      return;
    }

    if (categories.includes(newCategory.trim())) {
      setError('This category already exists');
      return;
    }

    try {
      setCategoryLoading(true);
      await addDoc(collection(db, 'categories'), {
        name: newCategory.trim()
      });
      setCategories(prev => [...prev, newCategory.trim()].sort());
      setNewCategory('');
      setSuccess('Category added successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error adding category:', error);
      setError('Failed to add category. Please try again.');
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryName) => {
    if (!window.confirm(`Are you sure you want to delete "${categoryName}"? This will not affect existing blog posts.`)) {
      return;
    }

    try {
      setCategoryLoading(true);
      // Find and delete the category document
      const categoriesRef = collection(db, 'categories');
      const q = query(categoriesRef);
      const querySnapshot = await getDocs(q);
      
      querySnapshot.forEach(async (docSnapshot) => {
        if (docSnapshot.data().name === categoryName) {
          await deleteDoc(doc(db, 'categories', docSnapshot.id));
        }
      });

      setCategories(prev => prev.filter(cat => cat !== categoryName));
      setSuccess('Category deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting category:', error);
      setError('Failed to delete category. Please try again.');
    } finally {
      setCategoryLoading(false);
    }
  };

  // Initialize edit mode if coming from dashboard
  useEffect(() => {
    if (location.state?.editMode && location.state?.blogPost) {
      const blogPost = location.state.blogPost;
      setEditMode(true);
      setBlogPostId(blogPost.id);
      
      // Populate form with existing blog post data
      setFormData({
        title: blogPost.title || '',
        content: blogPost.content || '',
        category: blogPost.category || '',
        tags: blogPost.tags ? blogPost.tags.join(', ') : '',
        excerpt: blogPost.excerpt || '',
        seoKeywords: blogPost.seoKeywords || '',
        metaDescription: blogPost.metaDescription || '',
        slug: blogPost.slug || generateSlug(blogPost.title || ''),
        mainImage: blogPost.images?.mainImage || null,
        mainImageAlt: blogPost.images?.mainImageAlt || '',
        bannerImage: blogPost.images?.bannerImage || null,
        bannerImageAlt: blogPost.images?.bannerImageAlt || '',
        contentImages: blogPost.images?.contentImages || [],
        contentImagesAlt: blogPost.images?.contentImagesAlt || []
      });
      
      // If slug exists, mark it as manually edited to preserve it
      if (blogPost.slug) {
        setSlugManuallyEdited(true);
      }

      // If meta description exists, mark it as manually edited to preserve it
      if (blogPost.metaDescription) {
        setMetaDescriptionManuallyEdited(true);
      }

      // Set preview images
      setPreviewImages({
        mainImage: blogPost.images?.mainImage || null,
        bannerImage: blogPost.images?.bannerImage || null,
        contentImages: blogPost.images?.contentImages || []
      });
    }
  }, [location.state]);

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current) {
      if (formData.content && editorRef.current.innerHTML !== formData.content) {
        editorRef.current.innerHTML = formData.content;
      } else if (!formData.content) {
        editorRef.current.innerHTML = '';
      }
    }
  }, [formData.content]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Generate SEO-friendly slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_-]+/g, '-')  // Replace spaces and underscores with hyphens
      .replace(/^-+|-+$/g, '');   // Remove leading/trailing hyphens
  };

  // Auto-generate slug from title (always use title as default)
  useEffect(() => {
    if (formData.title && !slugManuallyEdited) {
      const slug = generateSlug(formData.title);
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.title, slugManuallyEdited]);

  const handleSlugChange = (e) => {
    setSlugManuallyEdited(true);
    handleInputChange(e);
  };

  // Auto-generate meta description from excerpt (only if not manually edited)
  useEffect(() => {
    if (formData.excerpt && !formData.metaDescription && !editMode && !metaDescriptionManuallyEdited) {
      const metaDesc = formData.excerpt.substring(0, 160).trim();
      setFormData(prev => ({ ...prev, metaDescription: metaDesc }));
    }
  }, [formData.excerpt, editMode, metaDescriptionManuallyEdited]);

  // Auto-generate SEO keywords from content
  const generateSEOKeywords = () => {
    const stopWords = new Set([
      'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by',
      'from', 'as', 'is', 'was', 'are', 'were', 'been', 'be', 'have', 'has', 'had', 'do', 'does', 'did',
      'will', 'would', 'could', 'should', 'may', 'might', 'must', 'can', 'this', 'that', 'these', 'those',
      'i', 'you', 'he', 'she', 'it', 'we', 'they', 'what', 'which', 'who', 'whom', 'whose', 'where', 'when', 'why', 'how',
      'all', 'each', 'every', 'both', 'few', 'more', 'most', 'other', 'some', 'such', 'no', 'nor', 'not', 'only', 'own', 'same',
      'so', 'than', 'too', 'very', 'can', 'will', 'just', 'don', 'should', 'now'
    ]);

    // Combine title, excerpt, content, tags, and category
    const text = [
      formData.title,
      formData.excerpt,
      formData.content,
      formData.tags,
      formData.category
    ].filter(Boolean).join(' ').toLowerCase();

    // Remove markdown syntax and special characters
    const cleanText = text
      .replace(/[#*`_~\[\]()]/g, ' ') // Remove markdown characters
      .replace(/[^\w\s]/g, ' ') // Remove special characters
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();

    // Extract words
    const words = cleanText.split(/\s+/).filter(word => 
      word.length > 2 && // Minimum 3 characters
      !stopWords.has(word) && // Not a stop word
      !/^\d+$/.test(word) // Not just numbers
    );

    // Count word frequency
    const wordFreq = {};
    words.forEach(word => {
      wordFreq[word] = (wordFreq[word] || 0) + 1;
    });

    // Extract phrases (2-3 word combinations)
    const phrases = [];
    const wordsArray = cleanText.split(/\s+/);
    for (let i = 0; i < wordsArray.length - 1; i++) {
      const word1 = wordsArray[i];
      const word2 = wordsArray[i + 1];
      
      if (word1 && word2 && 
          word1.length > 2 && word2.length > 2 &&
          !stopWords.has(word1) && !stopWords.has(word2)) {
        phrases.push(`${word1} ${word2}`);
      }
    }

    // Combine single words and phrases, sort by frequency/relevance
    const keywords = new Set();
    
    // Add high-frequency single words
    const sortedWords = Object.entries(wordFreq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([word]) => word);
    
    sortedWords.forEach(word => keywords.add(word));
    
    // Add relevant phrases (limit to avoid too many)
    phrases.slice(0, 5).forEach(phrase => keywords.add(phrase));

    // Convert to comma-separated string
    const keywordString = Array.from(keywords).slice(0, 10).join(', ');
    
    return keywordString;
  };

  const handleGenerateKeywords = () => {
    if (!formData.title.trim() && !formData.content.trim()) {
      setError('Please enter at least a title or content to generate keywords');
      return;
    }

    const generatedKeywords = generateSEOKeywords();
    if (generatedKeywords) {
      setFormData(prev => ({
        ...prev,
        seoKeywords: generatedKeywords
      }));
      setSuccess('SEO keywords generated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError('Could not generate keywords. Please add more content.');
    }
  };

  // Convert image to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Compress image before converting to base64
  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to base64 with compression
          canvas.toBlob(
            (blob) => {
              const compressedReader = new FileReader();
              compressedReader.readAsDataURL(blob);
              compressedReader.onloadend = () => {
                resolve(compressedReader.result);
              };
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  // Format text using document.execCommand
  const formatText = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    updateEditorContent();
  };

  // Insert image at cursor position
  const insertImage = async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      try {
        // Compress image
        const base64 = await compressImage(file, 1200, 0.8);
        
        // Insert image at cursor position
        const img = document.createElement('img');
        img.src = base64;
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
        img.style.borderRadius = '8px';
        img.style.margin = '16px 0';
        img.style.display = 'block';
        
        // Insert image at cursor
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.deleteContents();
          range.insertNode(img);
          // Move cursor after image
          range.setStartAfter(img);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        } else {
          editorRef.current?.appendChild(img);
        }
        
        // Update form data
        updateEditorContent();
      } catch (error) {
        setError('Failed to insert image. Please try again.');
        console.error('Image insertion error:', error);
      }
    };
  };

  // Insert link
  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      formatText('createLink', url);
    }
  };

  // Update form data when editor content changes
  const updateEditorContent = () => {
    if (editorRef.current) {
      setFormData(prev => ({
        ...prev,
        content: editorRef.current.innerHTML
      }));
    }
  };

  // Handle editor input
  const handleEditorInput = () => {
    updateEditorContent();
    updateToolbarPosition();
  };

  // Handle paste to clean up pasted content
  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text');
    document.execCommand('insertText', false, text);
    updateEditorContent();
    updateToolbarPosition();
  };

  // Update toolbar position based on cursor/selection
  const updateToolbarPosition = () => {
    if (!editorRef.current || !toolbarRef.current) return;

    const selection = window.getSelection();
    if (selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const toolbar = toolbarRef.current;

    // Get the bottom position of the cursor/selection in viewport coordinates
    const cursorBottom = rect.bottom;
    
    // Position toolbar so its bottom edge aligns with cursor bottom
    // Toolbar is fixed to left side, so we use viewport coordinates
    const toolbarHeight = toolbar.offsetHeight || 400; // fallback height
    let topPosition = cursorBottom - toolbarHeight;
    
    // Ensure toolbar stays within viewport bounds
    const viewportHeight = window.innerHeight;
    const maxTop = viewportHeight - toolbarHeight - 20; // Leave some space at bottom
    const minTop = 100; // Leave space at top (accounting for navigation)
    
    // If cursor is near the top, position toolbar below cursor
    if (topPosition < minTop) {
      topPosition = cursorBottom + 10; // 10px below cursor
    }
    
    topPosition = Math.max(minTop, Math.min(topPosition, maxTop));
    
    toolbar.style.top = `${topPosition}px`;
    toolbar.style.transform = 'none'; // Override the translateY(-50%) when positioning dynamically
  };

  // Handle cursor movement and selection changes
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const handleSelectionChange = () => {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        updateToolbarPosition();
      }, 0);
    };

    const handleScroll = () => {
      updateToolbarPosition();
    };

    const handleKeyDown = (e) => {
      // Update position after key press
      setTimeout(() => {
        updateToolbarPosition();
      }, 0);
    };

    const handleMouseUp = () => {
      setTimeout(() => {
        updateToolbarPosition();
      }, 0);
    };

    // Listen for selection changes
    document.addEventListener('selectionchange', handleSelectionChange);
    editor.addEventListener('click', handleSelectionChange);
    editor.addEventListener('keyup', handleSelectionChange);
    editor.addEventListener('keydown', handleKeyDown);
    editor.addEventListener('mouseup', handleMouseUp);
    editor.addEventListener('scroll', handleScroll);
    editor.addEventListener('input', handleSelectionChange);

    // Initial position update
    setTimeout(() => {
      updateToolbarPosition();
    }, 100);

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      editor.removeEventListener('click', handleSelectionChange);
      editor.removeEventListener('keyup', handleSelectionChange);
      editor.removeEventListener('keydown', handleKeyDown);
      editor.removeEventListener('mouseup', handleMouseUp);
      editor.removeEventListener('scroll', handleScroll);
      editor.removeEventListener('input', handleSelectionChange);
    };
  }, []);

  const handleImageUpload = async (e, imageType) => {
    const files = e.target.files;
    if (!files.length) return;

    try {
      if (imageType === 'contentImages') {
        const newImages = [];
        const newPreviews = [];

        for (let file of Array.from(files)) {
          // Compress and convert to base64
          const base64 = await compressImage(file, 800, 0.7);
          newImages.push(base64);
          newPreviews.push(base64);
        }

        setFormData(prev => ({
          ...prev,
          contentImages: [...prev.contentImages, ...newImages]
        }));

        setPreviewImages(prev => ({
          ...prev,
          contentImages: [...prev.contentImages, ...newPreviews]
        }));
      } else {
        const file = files[0];
        
        // Different compression settings for different image types
        let maxWidth = imageType === 'mainImage' ? 1200 : 1600;
        let quality = imageType === 'mainImage' ? 0.8 : 0.85;
        
        const base64 = await compressImage(file, maxWidth, quality);

        setFormData(prev => ({
          ...prev,
          [imageType]: base64
        }));

        setPreviewImages(prev => ({
          ...prev,
          [imageType]: base64
        }));
      }
    } catch (error) {
      setError('Failed to process image. Please try again.');
      console.error('Image processing error:', error);
    }
  };

  const removeImage = (imageType, index = null) => {
    if (imageType === 'contentImages' && index !== null) {
      setFormData(prev => ({
        ...prev,
        contentImages: prev.contentImages.filter((_, i) => i !== index),
        contentImagesAlt: prev.contentImagesAlt.filter((_, i) => i !== index)
      }));
      setPreviewImages(prev => ({
        ...prev,
        contentImages: prev.contentImages.filter((_, i) => i !== index)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [imageType]: null,
        [`${imageType}Alt`]: ''
      }));
      setPreviewImages(prev => ({
        ...prev,
        [imageType]: null
      }));
    }
  };

  const handleImageAltChange = (imageType, value, index = null) => {
    if (imageType === 'contentImages' && index !== null) {
      setFormData(prev => {
        const newAltTexts = [...prev.contentImagesAlt];
        newAltTexts[index] = value;
        return {
          ...prev,
          contentImagesAlt: newAltTexts
        };
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [`${imageType}Alt`]: value
      }));
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (!formData.title.trim() || !formData.content.trim()) {
        throw new Error('Title and content are required');
      }

      const user = auth.currentUser;
      if (!user) {
        throw new Error('You must be logged in to create a post');
      }

      // Strip HTML tags for excerpt and meta description if content is HTML
      const stripHTML = (html) => {
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
      };

      // Prepare blog post data with base64 images
      const blogPostData = {
        title: formData.title.trim(),
        content: formData.content.trim(), // HTML content from rich text editor
        excerpt: formData.excerpt.trim() || stripHTML(formData.content).substring(0, 150) + '...',
        category: formData.category || 'Other',
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        seoKeywords: formData.seoKeywords.trim(),
        metaDescription: formData.metaDescription.trim() || formData.excerpt.trim() || stripHTML(formData.content).substring(0, 160).trim(),
        slug: formData.slug.trim() || generateSlug(formData.title),
        images: {
          mainImage: formData.mainImage || null,
          mainImageAlt: formData.mainImageAlt.trim() || '',
          bannerImage: formData.bannerImage || null,
          bannerImageAlt: formData.bannerImageAlt.trim() || '',
          contentImages: formData.contentImages || [],
          contentImagesAlt: formData.contentImagesAlt || []
        },
        updatedAt: serverTimestamp(),
        published: true
      };

      if (editMode) {
        // Update existing blog post
        await updateDoc(doc(db, 'blogposts', blogPostId), blogPostData);
        setSuccess('Blog post updated successfully! Redirecting...');
      } else {
        // Create new blog post
        const newBlogPost = {
          ...blogPostData,
          author: {
            uid: user.uid,
            name: user.displayName || 'Anonymous',
            email: user.email,
            photoURL: user.photoURL || null
          },
          createdAt: serverTimestamp(),
          likes: 0,
          views: 0,
          comments: []
        };
        await addDoc(collection(db, 'blogposts'), newBlogPost);
        setSuccess('Blog post created successfully! Redirecting...');
      }

      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);

    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      title: '',
      content: '',
      category: '',
      tags: '',
      excerpt: '',
      seoKeywords: '',
      metaDescription: '',
      slug: '',
      mainImage: null,
      mainImageAlt: '',
      bannerImage: null,
      bannerImageAlt: '',
      contentImages: [],
      contentImagesAlt: []
    });
    setPreviewImages({
      mainImage: null,
      bannerImage: null,
      contentImages: []
    });
    setSlugManuallyEdited(false);
    setMetaDescriptionManuallyEdited(false);
    setError('');
    setSuccess('');
  };

  return (
    <div className="blogpost-container">
      <form onSubmit={handleSubmit} className="blogpost-form">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Title */}
        <div className="form-group">
          <label htmlFor="title">Post Title *</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="Enter an engaging title for your blog post"
            required
          />
        </div>

        {/* Image Uploads Section */}
        <div className="images-section">
          <h3>Images</h3>
          <p className="info-text">📸 Images are automatically compressed and optimized for web performance</p>

          {/* Main Image */}
          <div className="image-upload-group">
            <label>Main Image (Featured)</label>
            <p className="image-description">This image will be displayed as the main featured image</p>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, 'mainImage')}
              className="file-input"
            />
            {previewImages.mainImage && (
              <>
                <div className="image-preview-container">
                  <img src={previewImages.mainImage} alt="Main preview" className="image-preview main" />
                  <button type="button" onClick={() => removeImage('mainImage')} className="remove-image-btn" title="Remove image">
                    ✕
                  </button>
                </div>
                <div className="image-alt-input">
                  <label htmlFor="mainImageAlt">Alt Text for Main Image (SEO)</label>
                  <input
                    type="text"
                    id="mainImageAlt"
                    name="mainImageAlt"
                    value={formData.mainImageAlt}
                    onChange={(e) => handleImageAltChange('mainImage', e.target.value)}
                    placeholder="Describe the image for accessibility and SEO"
                  />
                  <p className="alt-text-help">💡 Alt text helps search engines understand your image and improves accessibility</p>
                </div>
              </>
            )}
          </div>

          {/* Banner Image */}
          <div className="image-upload-group">
            <label>Banner Image</label>
            <p className="image-description">Wide banner image for the post header</p>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, 'bannerImage')}
              className="file-input"
            />
            {previewImages.bannerImage && (
              <>
                <div className="image-preview-container">
                  <img src={previewImages.bannerImage} alt="Banner preview" className="image-preview banner" />
                  <button type="button" onClick={() => removeImage('bannerImage')} className="remove-image-btn" title="Remove image">
                    ✕
                  </button>
                </div>
                <div className="image-alt-input">
                  <label htmlFor="bannerImageAlt">Alt Text for Banner Image (SEO)</label>
                  <input
                    type="text"
                    id="bannerImageAlt"
                    name="bannerImageAlt"
                    value={formData.bannerImageAlt}
                    onChange={(e) => handleImageAltChange('bannerImage', e.target.value)}
                    placeholder="Describe the image for accessibility and SEO"
                  />
                  <p className="alt-text-help">💡 Alt text helps search engines understand your image and improves accessibility</p>
                </div>
              </>
            )}
          </div>

          {/* Content Images */}
          <div className="image-upload-group">
            <label>Content Images</label>
            <p className="image-description">Multiple images to use within your blog content</p>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleImageUpload(e, 'contentImages')}
              className="file-input"
            />
            {previewImages.contentImages.length > 0 && (
              <div className="content-images-section">
                <div className="content-images-grid">
                  {previewImages.contentImages.map((img, index) => (
                    <div key={index} className="content-image-item">
                      <div className="image-preview-container small">
                        <img src={img} alt={`Content ${index + 1}`} className="image-preview content" />
                        <button
                          type="button"
                          onClick={() => removeImage('contentImages', index)}
                          className="remove-image-btn small"
                          title="Remove image"
                        >
                          ✕
                        </button>
                      </div>
                      <div className="image-alt-input small">
                        <label htmlFor={`contentImageAlt-${index}`}>Alt Text (Image {index + 1})</label>
                        <input
                          type="text"
                          id={`contentImageAlt-${index}`}
                          value={formData.contentImagesAlt[index] || ''}
                          onChange={(e) => handleImageAltChange('contentImages', e.target.value, index)}
                          placeholder="Describe this image"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Excerpt */}
        <div className="form-group">
          <label htmlFor="excerpt">Excerpt</label>
          <textarea
            id="excerpt"
            name="excerpt"
            value={formData.excerpt}
            onChange={handleInputChange}
            placeholder="Brief description that will appear in previews"
            rows="3"
          />
        </div>

        {/* Meta Description */}
        <div className="form-group">
          <label htmlFor="metaDescription">Meta Description (SEO)</label>
          <textarea
            id="metaDescription"
            name="metaDescription"
            value={formData.metaDescription}
            onChange={(e) => {
              setMetaDescriptionManuallyEdited(true);
              handleInputChange(e);
            }}
            placeholder="Description for search engines (155-160 characters recommended)"
            rows="3"
            maxLength={160}
          />
          <p className="info-text">
            {formData.metaDescription.length}/160 characters
            {formData.metaDescription.length > 0 && formData.metaDescription.length < 120 && 
              <span style={{color: '#f59e0b', marginLeft: '8px'}}> - Consider adding more detail (120-160 chars ideal)</span>}
            {formData.metaDescription.length === 0 && 
              <span style={{color: '#6b7280', marginLeft: '8px'}}> - Auto-generated from excerpt if left empty</span>}
          </p>
        </div>

        {/* URL Slug */}
        <div className="form-group">
          <label htmlFor="slug">URL Slug</label>
          <div className="slug-input-wrapper">
            <span className="slug-prefix">/blog/</span>
            <input
              type="text"
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleSlugChange}
              placeholder="url-friendly-slug"
              className="slug-input"
            />
          </div>
          <p className="info-text">
            SEO-friendly URL (auto-generated from title). Use lowercase letters, numbers, and hyphens only.
            {formData.slug && !/^[a-z0-9-]+$/.test(formData.slug) && 
              <span style={{color: '#ef4444', marginLeft: '8px'}}> - Invalid characters! Use only lowercase, numbers, and hyphens</span>}
          </p>
        </div>

        {/* Category and Tags */}
        <div className="form-row">
          <div className="form-group">
            <div className="category-header">
              <label htmlFor="category">Category</label>
              <button
                type="button"
                onClick={() => setShowCategoryManager(!showCategoryManager)}
                className="manage-categories-btn"
                title="Manage categories"
              >
                {showCategoryManager ? '✕ Close' : '⚙️ Manage'}
              </button>
            </div>
            
            {showCategoryManager && (
              <div className="category-manager">
                <h4>Manage Categories</h4>
                <div className="add-category-form">
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="Enter new category name"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCategory();
                      }
                    }}
                    className="new-category-input"
                  />
                  <button
                    type="button"
                    onClick={handleAddCategory}
                    disabled={categoryLoading || !newCategory.trim()}
                    className="add-category-btn"
                  >
                    Add
                  </button>
                </div>
                <div className="categories-list">
                  {categories.map(category => (
                    <div key={category} className="category-item">
                      <span>{category}</span>
                      <button
                        type="button"
                        onClick={() => handleDeleteCategory(category)}
                        disabled={categoryLoading}
                        className="delete-category-btn"
                        title="Delete category"
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              disabled={categoryLoading}
            >
              <option value="">Select a category</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="tags">Tags</label>
            <input
              type="text"
              id="tags"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
              placeholder="react, javascript, tutorial (comma separated)"
            />
          </div>
        </div>

        {/* SEO Keywords */}
        <div className="form-group">
          <div className="seo-keywords-header">
            <label htmlFor="seoKeywords">SEO Keywords</label>
            <button
              type="button"
              onClick={handleGenerateKeywords}
              className="generate-keywords-btn"
              title="Auto-generate SEO keywords from your content"
            >
              ✨ Auto Generate
            </button>
          </div>
          <input
            type="text"
            id="seoKeywords"
            name="seoKeywords"
            value={formData.seoKeywords}
            onChange={handleInputChange}
            placeholder="keyword1, keyword2, keyword3 (comma separated keywords for SEO)"
          />
          <p className="info-text">💡 Click "Auto Generate" to automatically extract keywords from your content, or enter them manually</p>
        </div>

        {/* Rich Text Editor */}
        <div className="form-group rich-editor-container">
          <label>Content *</label>
          <div className="rich-editor-wrapper">
            <div
              ref={editorRef}
              contentEditable
              className="rich-editor"
              onInput={handleEditorInput}
              onPaste={handlePaste}
              data-placeholder="Write your blog post content here... Click the image icon to insert images inline!"
              suppressContentEditableWarning={true}
            />
          </div>
          <div className="formatting-help">
            <p><strong>💡 Editor Tips:</strong> Use the toolbar to format your text. Click the image icon (🖼️) to insert images directly into your content. Images are automatically compressed for optimal performance!</p>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handleReset}
            className="reset-btn"
            disabled={loading}
          >
            Reset All
          </button>
          <button
            type="submit"
            className="submit-btn"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="loading-spinner"></span>
                {editMode ? 'Updating...' : 'Publishing...'}
              </>
            ) : (
              <>
                <span>{editMode ? '💾' : '🚀'}</span>
                {editMode ? 'Update Post' : 'Publish Post'}
              </>
            )}
          </button>
        </div>
      </form>
      
      {/* Floating Toolbar - Outside all containers */}
      <div ref={toolbarRef} className="formatting-toolbar-vertical">
        <button type="button" onClick={() => formatText('bold')} title="Bold" className="format-btn">
          <strong>B</strong>
        </button>
        <button type="button" onClick={() => formatText('italic')} title="Italic" className="format-btn">
          <em>I</em>
        </button>
        <button type="button" onClick={() => formatText('underline')} title="Underline" className="format-btn">
          <u>U</u>
        </button>
        <div className="toolbar-divider-vertical"></div>
        <button type="button" onClick={() => formatText('formatBlock', 'h1')} title="Heading 1" className="format-btn">
          H1
        </button>
        <button type="button" onClick={() => formatText('formatBlock', 'h2')} title="Heading 2" className="format-btn">
          H2
        </button>
        <button type="button" onClick={() => formatText('formatBlock', 'h3')} title="Heading 3" className="format-btn">
          H3
        </button>
        <div className="toolbar-divider-vertical"></div>
        <button type="button" onClick={() => formatText('insertUnorderedList')} title="Bullet List" className="format-btn">
          •
        </button>
        <button type="button" onClick={() => formatText('insertOrderedList')} title="Numbered List" className="format-btn">
          1.
        </button>
        <div className="toolbar-divider-vertical"></div>
        <button type="button" onClick={insertLink} title="Insert Link" className="format-btn">
          🔗
        </button>
        <button type="button" onClick={insertImage} title="Insert Image" className="format-btn">
          🖼️
        </button>
        <button type="button" onClick={() => formatText('formatBlock', 'blockquote')} title="Quote" className="format-btn">
          "
        </button>
      </div>
    </div>
  );
};

export default Blogpost;
